import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Plus, CheckCircle, Clock, XCircle, Camera, Broom } from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';
import { API_URL, categories, recurrenceLabel } from '../components/inbox/taskHelpers';
import { TaskItem } from '../components/inbox/TaskItem';
import { ForecastSection } from '../components/inbox/ForecastSection';
import { TaskSidebar } from '../components/inbox/TaskSidebar';
import { TaskModal } from '../components/inbox/TaskModal';
import { BillScanner } from '../components/inbox/BillScanner';

const emptyTask = {
  title: '', description: '', category: 'bill',
  amount: '', due_date: '', due_time: '', priority: 50, provider: '', recurrence: 'none'
};

export default function TaskInbox() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTask, setNewTask] = useState({ ...emptyTask });
  const [submitting, setSubmitting] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [findingDuplicates, setFindingDuplicates] = useState(false);
  const [mergingDuplicates, setMergingDuplicates] = useState(false);

  const handleFindDuplicates = async () => {
    setFindingDuplicates(true);
    setShowDuplicates(true);
    try {
      const res = await axios.get(`${API_URL}/api/tasks/duplicates`, { withCredentials: true });
      setDuplicateGroups(res.data.groups || []);
      if ((res.data.groups || []).length === 0) {
        toast.success(`No duplicates found across ${res.data.total_scanned} tasks.`);
        setShowDuplicates(false);
      }
    } catch (e) {
      toast.error('Could not find duplicates: ' + (e.response?.data?.detail || e.message));
      setShowDuplicates(false);
    } finally {
      setFindingDuplicates(false);
    }
  };

  const handleMergeAll = async () => {
    setMergingDuplicates(true);
    try {
      const payload = {
        groups: duplicateGroups.map(g => ({
          keeper_id: g.suggested_keeper,
          delete_ids: g.tasks.filter(t => t.task_id !== g.suggested_keeper).map(t => t.task_id),
        })),
      };
      const res = await axios.post(`${API_URL}/api/tasks/merge-duplicates`, payload, { withCredentials: true });
      toast.success(`Merged ${res.data.groups_merged} group(s), deleted ${res.data.deleted} duplicates.`);
      setShowDuplicates(false);
      setDuplicateGroups([]);
      fetchTasks();
    } catch (e) {
      toast.error('Merge failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setMergingDuplicates(false);
    }
  };

  const fetchTasks = useCallback(async () => {
    try {
      const params = filter !== 'all' ? `?category=${filter}` : '';
      const response = await axios.get(`${API_URL}/api/tasks${params}`, { withCredentials: true });
      setTasks(response.data);
    } catch (error) {
      console.error('Fetch tasks error:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  // Re-fetch when page becomes visible (e.g., switching tabs or back from Assistant)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchTasks();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchTasks]);

  const handleAddTask = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const taskData = {
        ...newTask,
        amount: newTask.amount ? parseFloat(newTask.amount) : null,
        due_date: newTask.due_date ? (
          newTask.due_time
            ? new Date(`${newTask.due_date}T${newTask.due_time}`).toISOString()
            : new Date(newTask.due_date).toISOString()
        ) : null,
        due_time: newTask.due_time || null,
        priority: parseInt(newTask.priority)
      };
      await axios.post(`${API_URL}/api/tasks`, taskData, { withCredentials: true });
      setShowAddModal(false);
      setNewTask({ ...emptyTask });
      if (taskData.recurrence && taskData.recurrence !== 'none') {
        toast.success(`Recurring ${taskData.recurrence} task created`);
      }
      fetchTasks();
    } catch (error) {
      console.error('Add task error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBillScanned = (extracted) => {
    setShowScanner(false);
    const scannedTask = {
      title: extracted.title || '',
      description: extracted.description || '',
      category: extracted.category || 'bill',
      amount: extracted.amount != null ? String(extracted.amount) : '',
      due_date: extracted.due_date || '',
      due_time: extracted.due_time || '',
      priority: 70,
      provider: extracted.provider || '',
      recurrence: 'none',
    };
    setNewTask(scannedTask);
    setShowAddModal(true);
    toast.success(`Bill detected: ${extracted.provider || 'Unknown'} — ${extracted.amount ? `$${extracted.amount}` : 'review details'}`);
  };


  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await axios.delete(`${API_URL}/api/tasks/${taskId}`, { withCredentials: true });
      fetchTasks();
    } catch (error) {
      console.error('Delete task error:', error);
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      const task = tasks.find(t => t.task_id === taskId);
      await axios.put(`${API_URL}/api/tasks/${taskId}`, { status: 'completed' }, { withCredentials: true });
      if (task?.recurrence && task.recurrence !== 'none') {
        toast.success(`Next ${recurrenceLabel(task.recurrence).toLowerCase()} occurrence auto-created`, {
          description: task.title, duration: 4000
        });
      }
      fetchTasks();
    } catch (error) {
      console.error('Complete task error:', error);
    }
  };

  const handleUndoComplete = async (taskId) => {
    try {
      await axios.put(`${API_URL}/api/tasks/${taskId}`, { status: 'pending' }, { withCredentials: true });
      fetchTasks();
    } catch (error) {
      console.error('Undo complete error:', error);
    }
  };

  const handleOpenEdit = (task) => {
    const dueDate = task.due_date ? new Date(task.due_date).toISOString().split('T')[0] : '';
    setEditTask({
      task_id: task.task_id,
      title: task.title || '',
      description: task.description || '',
      category: task.category || 'general',
      amount: task.amount != null ? String(task.amount) : '',
      due_date: dueDate,
      priority: task.priority || 50,
      provider: task.provider || '',
      recurrence: task.recurrence || 'none',
      currency: task.currency || 'USD'
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const updateData = {
        title: editTask.title,
        description: editTask.description,
        category: editTask.category,
        amount: editTask.amount ? parseFloat(editTask.amount) : null,
        due_date: editTask.due_date ? new Date(editTask.due_date).toISOString() : null,
        priority: parseInt(editTask.priority),
        provider: editTask.provider,
        recurrence: editTask.recurrence,
        currency: editTask.currency
      };
      await axios.put(`${API_URL}/api/tasks/${editTask.task_id}`, updateData, { withCredentials: true });
      setShowEditModal(false);
      setEditTask(null);
      if (updateData.recurrence && updateData.recurrence !== 'none') {
        toast.success(`Set as ${recurrenceLabel(updateData.recurrence).toLowerCase()} recurring`);
      }
      fetchTasks();
    } catch (error) {
      console.error('Edit task error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Derived data
  const activePending = tasks.filter(t => t.status === 'pending' && !t.is_forecast);
  const cancelledTasks = tasks.filter(t => t.status === 'cancelled' || t.subscription_status === 'cancelled');
  const forecastTasks = tasks.filter(t => t.status !== 'completed' && t.status !== 'cancelled' && t.is_forecast === true);
  const completedTasks = tasks.filter(t => t.status === 'completed' && t.subscription_status !== 'cancelled');
  const sortedActiveTasks = [...activePending, ...completedTasks, ...cancelledTasks];

  const amountsByCurrency = {};
  activePending.filter(t => t.amount).forEach(t => {
    const cur = t.currency || 'USD';
    amountsByCurrency[cur] = (amountsByCurrency[cur] || 0) + t.amount;
  });

  const forecastByCurrency = {};
  forecastTasks.filter(t => t.amount).forEach(t => {
    const cur = t.currency || 'USD';
    forecastByCurrency[cur] = (forecastByCurrency[cur] || 0) + t.amount;
  });

  const subscriptionsByCurrency = {};
  tasks.filter(t => t.category === 'subscription' && t.status === 'pending' && t.amount).forEach(t => {
    const cur = t.currency || 'USD';
    subscriptionsByCurrency[cur] = (subscriptionsByCurrency[cur] || 0) + t.amount;
  });

  return (
    <Layout>
      <div className="p-4 lg:p-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="display-lg text-4xl">Task Inbox</h1>
            <p className="body-lg mt-2">Manage your digital life with automated precision.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleFindDuplicates}
              disabled={findingDuplicates}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#fff7ed] border border-[#fed7aa] text-[#c2410c] font-semibold text-sm hover:bg-[#ffedd5] transition-colors disabled:opacity-60"
              data-testid="find-duplicates-btn"
              title="Find and merge duplicate tasks"
            >
              <Broom size={18} weight="duotone" />
              <span className="hidden sm:inline">{findingDuplicates ? 'Scanning…' : 'Clean duplicates'}</span>
            </button>
            <button
              onClick={() => setShowScanner(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#faf8ff] border border-[#e0d4f5] text-[#7c3aed] font-semibold text-sm hover:bg-[#ede9fe] transition-colors"
              data-testid="scan-bill-btn"
            >
              <Camera size={18} weight="duotone" />
              <span className="hidden sm:inline">Scan Bill</span>
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary flex items-center gap-2"
              data-testid="add-task-btn"
            >
              <Plus size={20} weight="bold" />
              Add Task
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Filters */}
            <div className="flex flex-wrap gap-2">
              {categories.map(cat => (
                <button
                  key={cat.value}
                  onClick={() => setFilter(cat.value)}
                  className={`px-4 py-2 rounded-full font-medium text-sm transition-all ${
                    filter === cat.value
                      ? 'bg-[#005bc1] text-white'
                      : 'bg-[#f2f4f6] text-[#596065] hover:bg-[#ebeef2]'
                  }`}
                  data-testid={`filter-${cat.value}`}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Tasks List */}
            <div className="surface-card overflow-hidden">
              <div className="hidden sm:grid grid-cols-12 gap-4 p-4 bg-[#f2f4f6]">
                <div className="col-span-5 label-md">Task Details</div>
                <div className="col-span-2 label-md">Due Date</div>
                <div className="col-span-2 label-md">Value</div>
                <div className="col-span-2 label-md">Priority</div>
                <div className="col-span-1"></div>
              </div>

              {loading ? (
                <div className="p-12 text-center">
                  <div className="animate-spin w-8 h-8 border-4 border-[#ebeef2] border-t-[#005bc1] rounded-full mx-auto mb-3"></div>
                  <p className="body-lg text-sm">Loading tasks...</p>
                </div>
              ) : tasks.length === 0 ? (
                <div className="p-12 text-center">
                  <Clock size={48} className="mx-auto mb-4 text-[#acb3b8]" />
                  <p className="title-md">No tasks found</p>
                  <p className="body-lg text-sm mt-2">Add a task or seed demo data from the dashboard</p>
                </div>
              ) : (
                <div className="divide-y divide-[#f2f4f6]">
                  {sortedActiveTasks.map((task, idx) => {
                    const isCompleted = task.status === 'completed';
                    const isCancelled = task.status === 'cancelled' || task.subscription_status === 'cancelled';
                    const showCompletedDivider = isCompleted && idx === activePending.length && completedTasks.length > 0;
                    const showCancelledDivider = isCancelled && idx === activePending.length + completedTasks.length && cancelledTasks.length > 0;

                    return (
                      <div key={task.task_id}>
                        {showCompletedDivider && (
                          <div className="flex items-center gap-3 px-5 py-3 bg-[#f9faf9]">
                            <CheckCircle size={16} className="text-[#0f6b3a]" weight="fill" />
                            <span className="label-md text-xs text-[#596065] uppercase tracking-wider">Completed ({completedTasks.length})</span>
                            <div className="flex-1 h-px bg-[#ebeef2]"></div>
                          </div>
                        )}
                        {showCancelledDivider && (
                          <div className="flex items-center gap-3 px-5 py-3 bg-[#fef2f2]">
                            <XCircle size={16} className="text-[#9f1239]" weight="fill" />
                            <span className="label-md text-xs text-[#9f1239] uppercase tracking-wider">Cancelled ({cancelledTasks.length})</span>
                            <div className="flex-1 h-px bg-[#fecdd3]"></div>
                          </div>
                        )}
                        <TaskItem
                          task={task}
                          onComplete={handleCompleteTask}
                          onUndo={handleUndoComplete}
                          onEdit={handleOpenEdit}
                          onDelete={handleDeleteTask}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <ForecastSection
              forecastTasks={forecastTasks}
              forecastByCurrency={forecastByCurrency}
              onEdit={handleOpenEdit}
              onDelete={handleDeleteTask}
            />
          </div>

          <TaskSidebar
            subscriptionsByCurrency={subscriptionsByCurrency}
            amountsByCurrency={amountsByCurrency}
            activePendingCount={activePending.length}
            forecastTasks={forecastTasks}
            forecastByCurrency={forecastByCurrency}
          />
        </div>
      </div>

      <TaskModal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        task={newTask}
        setTask={setNewTask}
        onSubmit={handleAddTask}
        submitting={submitting}
        mode="add"
      />

      <TaskModal
        open={showEditModal}
        onOpenChange={(open) => { setShowEditModal(open); if (!open) setEditTask(null); }}
        task={editTask}
        setTask={setEditTask}
        onSubmit={handleSaveEdit}
        submitting={submitting}
        mode="edit"
      />

      <Toaster position="bottom-right" richColors />

      {showScanner && (
        <BillScanner
          onExtracted={handleBillScanned}
          onClose={() => setShowScanner(false)}
        />
      )}

      {showDuplicates && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowDuplicates(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-[#ebeef2]">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="display-md text-2xl">Duplicate tasks</h2>
                  <p className="text-sm text-[#596065] mt-1">
                    {duplicateGroups.length} group{duplicateGroups.length === 1 ? '' : 's'} found. We'll keep the highlighted version and delete the rest.
                  </p>
                </div>
                <button onClick={() => setShowDuplicates(false)} className="text-[#596065] hover:text-[#2d3338] p-2" data-testid="close-duplicates-modal">
                  <XCircle size={24} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {findingDuplicates && (
                <div className="flex items-center justify-center py-12 text-[#596065]">
                  <Clock size={20} className="animate-spin mr-2" />
                  Analyzing your tasks with AI…
                </div>
              )}
              {!findingDuplicates && duplicateGroups.map((g, idx) => (
                <div key={idx} className="border border-[#ebeef2] rounded-xl overflow-hidden">
                  {g.tasks.map(t => {
                    const isKeeper = t.task_id === g.suggested_keeper;
                    return (
                      <div
                        key={t.task_id}
                        className={`p-4 flex items-start justify-between gap-4 ${
                          isKeeper ? 'bg-[#ecfdf5] border-b border-[#d1fae5]' : 'bg-white border-b border-[#ebeef2] last:border-b-0'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-[#2d3338] truncate">{t.title}</span>
                            {isKeeper && <span className="tag tag-success text-[10px]">KEEP</span>}
                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                              t.status === 'completed' ? 'bg-[#a3f5b8] text-[#0f6b3a]' :
                              t.status === 'cancelled' ? 'bg-[#fee2e2] text-[#991b1b]' :
                              'bg-[#fef3c7] text-[#92400e]'
                            }`}>{t.status || 'pending'}</span>
                          </div>
                          <div className="text-xs text-[#596065] mt-1">
                            {t.provider && <span>{t.provider} · </span>}
                            {t.amount && <span>{t.currency} {t.amount} · </span>}
                            {t.due_date && <span>Due {t.due_date.substring(0, 10)}</span>}
                          </div>
                          <div className="text-[10px] text-[#acb3b8] mt-1">
                            Source: {t.source || 'manual'} · Created {t.created_at?.substring(0, 10)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
              {!findingDuplicates && duplicateGroups.length === 0 && (
                <p className="text-center text-[#596065] py-8">No duplicates found.</p>
              )}
            </div>

            {duplicateGroups.length > 0 && (
              <div className="p-6 border-t border-[#ebeef2] flex items-center justify-between gap-4">
                <p className="text-xs text-[#596065]">
                  Will delete {duplicateGroups.reduce((a, g) => a + g.tasks.length - 1, 0)} duplicate task(s).
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowDuplicates(false)}
                    className="px-4 py-2 rounded-lg text-sm font-semibold text-[#596065] hover:bg-[#f2f4f6]"
                    data-testid="cancel-merge-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleMergeAll}
                    disabled={mergingDuplicates}
                    className="btn-primary text-sm disabled:opacity-60"
                    data-testid="confirm-merge-btn"
                  >
                    {mergingDuplicates ? 'Merging…' : 'Merge all'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
